/**
 * Column Factory Functions
 *
 * Helper functions to create common DataGrid column types with consistent styling.
 * Reduces boilerplate when defining columns for DataGridCard.
 */

import {
  createTableColumn,
  TableCellLayout,
  TableColumnDefinition,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  tokens,
} from '@fluentui/react-components';
import { MoreHorizontal20Regular } from '@fluentui/react-icons';
import React, { createElement, ReactNode } from 'react';

/**
 * Creates a simple text column
 */
export interface TextColumnConfig<T> {
  columnId: string;
  header: string;
  getValue: (item: T) => string;
  sortable?: boolean;
  compare?: (a: T, b: T) => number;
}

export function createTextColumn<T>(config: TextColumnConfig<T>): TableColumnDefinition<T> {
  return createTableColumn<T>({
    columnId: config.columnId,
    compare: config.sortable !== false
      ? config.compare ?? ((a, b) => config.getValue(a).localeCompare(config.getValue(b)))
      : undefined,
    renderHeaderCell: () => config.header,
    renderCell: (item) =>
      createElement(TableCellLayout, null, config.getValue(item)),
  });
}

/**
 * Creates a status column with colored badge
 */
export type StatusType = 'success' | 'warning' | 'danger' | 'informative';

export interface StatusColumnConfig<T> {
  columnId: string;
  header: string;
  getStatus: (item: T) => { label: string; type: StatusType };
  sortable?: boolean;
}

const statusAppearanceMap: Record<StatusType, 'success' | 'warning' | 'danger' | 'informative'> = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  informative: 'informative',
};

export function createStatusColumn<T>(config: StatusColumnConfig<T>): TableColumnDefinition<T> {
  return createTableColumn<T>({
    columnId: config.columnId,
    compare: config.sortable !== false
      ? (a, b) => config.getStatus(a).label.localeCompare(config.getStatus(b).label)
      : undefined,
    renderHeaderCell: () => config.header,
    renderCell: (item) => {
      const status = config.getStatus(item);
      return createElement(
        TableCellLayout,
        null,
        createElement(Badge, {
          appearance: 'filled',
          color: statusAppearanceMap[status.type],
        }, status.label)
      );
    },
  });
}

/**
 * Creates a status column with colored dot (like TemplatesPage)
 */
export interface StatusDotColumnConfig<T> {
  columnId: string;
  header: string;
  getStatus: (item: T) => { label: string; type: 'active' | 'pending' | 'inactive' };
  sortable?: boolean;
}

const statusDotColors = {
  active: tokens.colorStatusSuccessForeground1,
  pending: tokens.colorStatusWarningForeground1,
  inactive: tokens.colorStatusDangerForeground1,
};

export function createStatusDotColumn<T>(config: StatusDotColumnConfig<T>): TableColumnDefinition<T> {
  return createTableColumn<T>({
    columnId: config.columnId,
    compare: config.sortable !== false
      ? (a, b) => config.getStatus(a).label.localeCompare(config.getStatus(b).label)
      : undefined,
    renderHeaderCell: () => config.header,
    renderCell: (item) => {
      const status = config.getStatus(item);
      return createElement(
        TableCellLayout,
        null,
        createElement(
          'span',
          { style: { display: 'inline-flex', alignItems: 'center', fontSize: '14px' } },
          createElement('span', {
            style: {
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              display: 'inline-block',
              marginRight: '8px',
              backgroundColor: statusDotColors[status.type],
            },
          }),
          status.label
        )
      );
    },
  });
}

/**
 * Creates an actions column with dropdown menu
 */
export interface ActionConfig {
  label: string;
  icon?: ReactNode;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
}

export interface ActionsColumnConfig<T> {
  columnId?: string;
  header?: string;
  getActions: (item: T) => ActionConfig[];
}

export function createActionsColumn<T>(config: ActionsColumnConfig<T>): TableColumnDefinition<T> {
  return createTableColumn<T>({
    columnId: config.columnId ?? 'actions',
    renderHeaderCell: () => config.header ?? 'Actions',
    renderCell: (item) => {
      const actions = config.getActions(item);
      return createElement(
        TableCellLayout,
        null,
        createElement(
          Menu,
          null,
          createElement(
            MenuTrigger,
            { disableButtonEnhancement: true },
            createElement(Button, {
              appearance: 'subtle',
              icon: createElement(MoreHorizontal20Regular),
              size: 'small',
              'aria-label': 'Actions',
            })
          ),
          createElement(
            MenuPopover,
            null,
            createElement(
              MenuList,
              null,
              ...actions.map((action, index) =>
                createElement(MenuItem, {
                  key: index,
                  ...(action.icon ? { icon: action.icon as React.JSX.Element } : {}),
                  onClick: (event: React.MouseEvent) => {
                    event.stopPropagation();
                    action.onClick();
                  },
                  disabled: action.disabled,
                }, action.label)
              )
            )
          )
        )
      );
    },
  });
}

/**
 * Creates a column with hover-triggered row actions menu
 *
 * The action button is hidden by default and appears on row hover.
 * Requires useDataGridStyles to be applied to the DataGrid for the hover effect.
 *
 * @example
 * ```tsx
 * const userColumn = createColumnWithRowActions<User>({
 *   columnId: 'user',
 *   header: 'User',
 *   renderContent: (user) => (
 *     <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
 *       <Avatar name={user.displayName} size={24} />
 *       <span>{user.displayName}</span>
 *     </div>
 *   ),
 *   getActions: (user) => [
 *     { label: 'Edit', onClick: () => handleEdit(user) },
 *     { label: 'Delete', onClick: () => handleDelete(user) },
 *   ],
 *   getAriaLabel: (user) => `Actions for ${user.displayName}`,
 * });
 * ```
 */
export interface ColumnWithRowActionsConfig<T> {
  columnId: string;
  header: string;
  renderContent: (item: T) => ReactNode;
  getActions: (item: T) => ActionConfig[];
  getAriaLabel?: (item: T) => string;
  compare?: (a: T, b: T) => number;
}

export function createColumnWithRowActions<T>(config: ColumnWithRowActionsConfig<T>): TableColumnDefinition<T> {
  return createTableColumn<T>({
    columnId: config.columnId,
    compare: config.compare,
    renderHeaderCell: () => config.header,
    renderCell: (item) => {
      const actions = config.getActions(item);
      // Use simple flex div like TreeFileBrowser - no TableCellLayout wrapper
      return createElement(
        'div',
        {
          style: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            minWidth: 0,
          },
        },
        // Content area - inline with action button
        config.renderContent(item),
        // Action menu - marginLeft:auto pushes to right edge
        createElement(
          Menu,
          null,
          createElement(
            MenuTrigger,
            { disableButtonEnhancement: true },
            createElement(Button, {
              appearance: 'subtle',
              icon: createElement(MoreHorizontal20Regular),
              size: 'small',
              className: 'row-action-button',
              style: { marginLeft: 'auto', flexShrink: 0 },
              'aria-label': config.getAriaLabel?.(item) ?? 'Actions',
              onClick: (event: React.MouseEvent) => event.stopPropagation(),
            })
          ),
            createElement(
              MenuPopover,
              null,
              createElement(
                MenuList,
                null,
                ...actions.map((action, index) =>
                  createElement(MenuItem, {
                    key: index,
                    ...(action.icon ? { icon: action.icon as React.JSX.Element } : {}),
                    onClick: (event: React.MouseEvent) => {
                      event.stopPropagation();
                      action.onClick();
                    },
                    disabled: action.disabled,
                  }, action.label)
                )
              )
            )
          )
      );
    },
  });
}

/**
 * Creates a date column with formatted output
 */
export interface DateColumnConfig<T> {
  columnId: string;
  header: string;
  getDate: (item: T) => Date | null | undefined;
  format?: 'short' | 'medium' | 'long' | 'relative';
  sortable?: boolean;
}

function formatDate(date: Date | null | undefined, format: DateColumnConfig<unknown>['format'] = 'medium'): string {
  if (!date) return '—';

  switch (format) {
    case 'short':
      return date.toLocaleDateString();
    case 'medium':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'long':
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    case 'relative':
      return formatRelativeDate(date);
    default:
      return date.toLocaleDateString();
  }
}

function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function createDateColumn<T>(config: DateColumnConfig<T>): TableColumnDefinition<T> {
  return createTableColumn<T>({
    columnId: config.columnId,
    compare: config.sortable !== false
      ? (a, b) => {
          const dateA = config.getDate(a);
          const dateB = config.getDate(b);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB.getTime() - dateA.getTime();
        }
      : undefined,
    renderHeaderCell: () => config.header,
    renderCell: (item) =>
      createElement(
        TableCellLayout,
        null,
        createElement('span', {
          style: { fontSize: '14px', color: tokens.colorNeutralForeground2 },
        }, formatDate(config.getDate(item), config.format))
      ),
  });
}
